/**
 * Agentes — um card por agente (Ativador de promoções, Monitor de margens, Coleta do Score,
 * Coleta de promoções, Promoções da Shopee, Oferta relâmpago da Shopee) com situação, última e próxima rodada, números, avisos, interruptor e
 * "rodar agora"; página do agente com histórico e log; fila de aprovação do Monitor de margens.
 * Pedido da Mariana, 24/09/2026. Fora do SourceScopeGuard: só fala com o serviço 8106.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, FormControl, InputLabel, Link, MenuItem, Select, Stack, Switch,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import { DomainPageShell } from '../components/DomainPageShell'
import {
  AgentesApiError, decidirMonitor, enviarEmailAgora, fetchAgente, fetchAgentes, fetchFilaMonitor, pausar, retomar, rodarAgora, urlAprovadosCsv,
  type Agente, type AlertaAgente, type EmailInfo, type FilaMonitor, type ItemMonitor,
} from './agentes/agentesService'
import { ESFORCO, SEMAFORO, acertoMonitor, brl, filtrarMonitor, linhaNumeros, pct, quando, relativo, resumoMonitor, type FiltroMonitor } from './agentes/logic'

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e))

// ---------------------------------------------------------------- card

function CardAgente({ a, escreve, onMudou, onAbrir }: { a: Agente; escreve: boolean; onMudou: () => void; onAbrir: () => void }) {
  const [busy, setBusy] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const s = SEMAFORO[a.semaforo] || SEMAFORO.vermelho
  const acao = async (nome: string, fn: () => Promise<unknown>) => {
    setBusy(nome)
    setErro(null)
    try {
      await fn()
      onMudou()
    } catch (e) {
      setErro(msg(e))
    } finally {
      setBusy(null)
    }
  }
  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{a.nome}</Typography>
            <Typography variant="caption" color="text.secondary">{a.agenda}</Typography>
          </Box>
          <Chip size="small" color={s.cor} label={s.rotulo} sx={{ flexShrink: 0 }} />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>{a.objetivo}</Typography>
        <Divider />
        <Stack spacing={0.25}>
          <Typography variant="body2"><b>Última rodada:</b> {quando(a.ultima_rodada)} {a.ultima_rodada ? <Box component="span" sx={{ color: 'text.disabled' }}>({relativo(a.ultima_rodada)})</Box> : null}</Typography>
          <Typography variant="body2"><b>Próxima:</b> {a.pausado ? 'pausado — não roda' : `${quando(a.proxima)} ${a.proxima ? `(${relativo(a.proxima)})` : ''}`}</Typography>
          {linhaNumeros(a) ? <Typography variant="body2"><b>Fez:</b> {linhaNumeros(a)}</Typography> : <Typography variant="body2" color="text.disabled">Sem números da última rodada.</Typography>}
        </Stack>
        {a.avisos.length ? (
          <Stack spacing={0.25}>
            {a.avisos.map((x) => <Typography key={x} variant="caption" sx={{ color: 'warning.main' }}>⚠ {x}</Typography>)}
          </Stack>
        ) : null}
        {erro ? <Alert severity="error" onClose={() => setErro(null)} sx={{ py: 0 }}>{erro}</Alert> : null}
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Tooltip title={escreve ? (a.pausado ? 'Religar: volta a rodar na próxima agenda' : 'Pausar: a próxima rodada não roda até você religar') : 'Serviço em modo leitura'}>
            <span>
              <Switch size="small" checked={!a.pausado} disabled={!escreve || busy != null} onChange={() => void acao('pausa', () => (a.pausado ? retomar(a.id) : pausar(a.id)))} />
            </span>
          </Tooltip>
          <Typography variant="caption">{a.pausado ? 'pausado' : 'ligado'}</Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" disabled={!escreve || busy != null || a.semaforo === 'rodando'} onClick={() => void acao('rodar', () => rodarAgora(a.id))}>
            {busy === 'rodar' ? <CircularProgress size={14} /> : a.semaforo === 'rodando' ? 'rodando…' : 'Rodar agora'}
          </Button>
          <Button size="small" variant="contained" onClick={onAbrir}>Abrir</Button>
        </Stack>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------- fila do monitor

function FilaMonitorMargens({ escreve }: { escreve: boolean }) {
  const [fila, setFila] = useState<FilaMonitor | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [filtro, setFiltro] = useState<FiltroMonitor>({ conta: '', familia: '', estado: 'pendentes', regime: '' })
  const [motivo, setMotivo] = useState<Record<string, string>>({})
  const [alvo, setAlvo] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState<string | null>(null)

  const carregar = useCallback(() => {
    fetchFilaMonitor().then(setFila).catch((e) => setErro(msg(e)))
  }, [])
  useEffect(() => {
    carregar()
  }, [carregar])

  const itens = useMemo(() => fila?.itens ?? [], [fila])
  const lista = useMemo(() => filtrarMonitor(itens, filtro), [itens, filtro])
  const res = useMemo(() => resumoMonitor(itens), [itens])
  const ac = useMemo(() => acertoMonitor(itens), [itens])
  const familias = useMemo(() => Object.entries(fila?.familias ?? {}).sort((a, b) => b[1].n - a[1].n), [fila])

  const decidir = async (i: ItemMonitor, decisao: 'aprovado' | 'reprovado') => {
    setBusy(i.mlb)
    setErro(null)
    try {
      const pa = alvo[i.mlb] ? Number(alvo[i.mlb].replace(',', '.')) : null
      await decidirMonitor({ mlb: i.mlb, decisao, motivo: motivo[i.mlb] || '', preco_alvo: pa && pa > 0 ? pa : null })
      carregar()
    } catch (e) {
      setErro(msg(e))
    } finally {
      setBusy(null)
    }
  }

  if (!fila) return erro ? <Alert severity="error">{erro}</Alert> : <CircularProgress size={20} />
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Anúncios cuja margem <b>ajustada no preço cheio</b> ficou abaixo de <b>{fila.piso_pct ?? 23}%</b> na coleta de {quando(fila.gerado_em)}. O preço-alvo é o menor
        preço terminado em ,90 que devolve o piso, já com o frete relido no preço novo. Aprove o que faz sentido, reprove com o motivo: é isso que ensina o agente. Ele
        não escreve no Mercado Livre; a lista dos aprovados sai em CSV para a planilha de edição em massa.
        {fila.motivo ? <><br /><b>{fila.motivo}</b></> : null}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <Chip label={`${res.total} abaixo do piso`} />
        <Chip color="warning" variant={filtro.estado === 'pendentes' ? 'filled' : 'outlined'} label={`${res.pendentes} esperando você`} onClick={() => setFiltro((f) => ({ ...f, estado: 'pendentes' }))} />
        <Chip color="success" variant={filtro.estado === 'aprovados' ? 'filled' : 'outlined'} label={`${res.aprovados} aprovados`} onClick={() => setFiltro((f) => ({ ...f, estado: 'aprovados' }))} />
        <Chip color="error" variant={filtro.estado === 'reprovados' ? 'filled' : 'outlined'} label={`${res.reprovados} reprovados`} onClick={() => setFiltro((f) => ({ ...f, estado: 'reprovados' }))} />
        <Chip variant={filtro.estado === 'todos' ? 'filled' : 'outlined'} label="todos" onClick={() => setFiltro((f) => ({ ...f, estado: 'todos' }))} />
        <Typography variant="caption" color="text.secondary">
          · aumento mediano {pct(res.medianaAumento)} · {res.negativas} com margem negativa (provável erro de cadastro) · {res.cruzam} cruzam o degrau de R$ 79
          {ac.decididos ? ` · acerto do agente até agora: ${pct(ac.taxa, 0)} (${ac.acertos} de ${ac.decididos})` : ''}
        </Typography>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="ag-conta">Conta</InputLabel>
          <Select labelId="ag-conta" label="Conta" value={filtro.conta} onChange={(e) => setFiltro((f) => ({ ...f, conta: e.target.value }))}>
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="Matriz">Matriz</MenuItem>
            <MenuItem value="Extrema">Extrema</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="ag-fam">Família</InputLabel>
          <Select labelId="ag-fam" label="Família" value={filtro.familia} onChange={(e) => setFiltro((f) => ({ ...f, familia: e.target.value }))}>
            <MenuItem value="">Todas</MenuItem>
            {familias.map(([k, v]) => <MenuItem key={k} value={k}>{k} ({v.n} · MC média {pct(v.mc_media)} · +{pct(v.aumento_medio)})</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="ag-reg">Regime do frete</InputLabel>
          <Select labelId="ag-reg" label="Regime do frete" value={filtro.regime} onChange={(e) => setFiltro((f) => ({ ...f, regime: e.target.value }))}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="fica abaixo de R$ 79">fica abaixo de R$ 79</MenuItem>
            <MenuItem value="cruza para frete grátis">cruza para frete grátis</MenuItem>
            <MenuItem value="já era frete grátis">já era frete grátis</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Button component={Link} href={urlAprovadosCsv()} variant="outlined" size="small" disabled={!res.aprovados}>Baixar aprovados (CSV)</Button>
      </Stack>
      {erro ? <Alert severity="error" onClose={() => setErro(null)}>{erro}</Alert> : null}
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1100, '& th': { fontSize: 11, textTransform: 'uppercase', color: 'text.secondary', whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell>Anúncio</TableCell>
              <TableCell>Família</TableCell>
              <TableCell align="right">Preço hoje</TableCell>
              <TableCell align="right">MC hoje</TableCell>
              <TableCell align="right">Preço-alvo</TableCell>
              <TableCell align="right">MC nova</TableCell>
              <TableCell align="right">Aumento</TableCell>
              <TableCell>Frete</TableCell>
              <TableCell>Decisão</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lista.slice(0, 200).map((i) => {
              const d = i.decisao
              const esf = i.esforco ? ESFORCO[i.esforco] : null
              return (
                <TableRow key={i.mlb} hover sx={{ opacity: d ? 0.75 : 1, bgcolor: i.mc_negativa ? 'rgba(255,0,0,0.04)' : i.apelido === 'Extrema' ? 'rgba(255,242,204,0.35)' : undefined }}>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" noWrap title={i.titulo || i.mlb} sx={{ fontWeight: 600 }}>{i.titulo || i.mlb}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {i.mlb} · {i.apelido}{i.sku ? ` · ${i.sku}` : ''} · estoque {i.estoque ?? '—'} · {i.pedidos_30d ?? 0} ped/30d{i.curva ? ` · curva ${i.curva}` : ''}{i.n_promo_ativa ? ` · ${i.n_promo_ativa} promo ativa` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={i.familia} /></TableCell>
                  <TableCell align="right">{brl(i.preco_hoje)}</TableCell>
                  <TableCell align="right" sx={{ color: i.mc_negativa ? 'error.main' : undefined }}>
                    <Tooltip title={`CMV ${brl(i.cmv)} (${i.cmv_origem || '?'}) · comissão ${pct(i.comissao_pct)} · imposto ${pct(i.imposto_pct)} (${i.imposto_origem || '?'}) · frete ${brl(i.frete_hoje)}`}>
                      <span>{pct(i.mc_hoje_pct)}</span>
                    </Tooltip>
                  </TableCell>
                  {i.impossivel ? (
                    <TableCell colSpan={4}><Typography variant="caption" color="error.main">Sem preço possível: {i.motivo}</Typography></TableCell>
                  ) : (
                    <>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {d ? brl(d.preco_alvo ?? i.preco_alvo) : (
                          <TextField size="small" value={alvo[i.mlb] ?? String(i.preco_alvo ?? '')} onChange={(e) => setAlvo((m) => ({ ...m, [i.mlb]: e.target.value }))} inputProps={{ style: { textAlign: 'right', width: 84, padding: '4px 6px' } }} />
                        )}
                        {d?.alterou_alvo ? <Typography variant="caption" color="text.secondary" display="block">proposto {brl(i.preco_alvo)}</Typography> : null}
                      </TableCell>
                      <TableCell align="right">{pct(i.mc_nova_pct)}</TableCell>
                      <TableCell align="right">
                        {esf ? <Chip size="small" color={esf.cor} label={`+${pct(i.aumento_pct)}`} title={esf.rotulo} /> : pct(i.aumento_pct)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: i.regime_frete === 'cruza para frete grátis' ? 'warning.main' : undefined }}>{i.regime_frete}</Typography>
                        <Typography variant="caption" display="block" color="text.disabled">{brl(i.frete_hoje)} → {brl(i.frete_no_alvo)} ({i.frete_origem})</Typography>
                      </TableCell>
                    </>
                  )}
                  <TableCell sx={{ minWidth: 260 }}>
                    {d ? (
                      <Typography variant="caption" sx={{ color: d.decisao === 'aprovado' ? 'success.main' : 'error.main' }}>
                        {d.decisao === 'aprovado' ? '✓ aprovado' : '✗ reprovado'} {quando(d.quando)}{d.motivo ? ` — ${d.motivo}` : ''}
                      </Typography>
                    ) : (
                      <Stack spacing={0.5}>
                        <TextField size="small" placeholder="motivo (obrigatório para reprovar)" value={motivo[i.mlb] || ''} onChange={(e) => setMotivo((m) => ({ ...m, [i.mlb]: e.target.value }))} inputProps={{ style: { fontSize: 12, padding: '4px 6px' } }} />
                        <Stack direction="row" spacing={0.5}>
                          <Button size="small" variant="contained" color="success" disabled={!escreve || busy != null || i.impossivel} onClick={() => void decidir(i, 'aprovado')}>Aprovar</Button>
                          <Button size="small" variant="outlined" color="error" disabled={!escreve || busy != null || (motivo[i.mlb] || '').trim().length < 3} onClick={() => void decidir(i, 'reprovado')}>Reprovar</Button>
                        </Stack>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        {lista.length > 200 ? <Typography variant="caption" color="text.secondary">Mostrando 200 de {lista.length}. Use os filtros.</Typography> : null}
        {!lista.length ? <Typography variant="body2" color="text.disabled" sx={{ p: 2 }}>Nada nesta seleção.</Typography> : null}
      </Box>
    </Stack>
  )
}

// ---------------------------------------------------------------- avisos com link (Promoções da Shopee)

const GRAVIDADE: Record<AlertaAgente['gravidade'], { rotulo: string; cor: 'error' | 'warning' | 'default' }> = {
  critico: { rotulo: 'Crítico', cor: 'error' },
  atencao: { rotulo: 'Atenção', cor: 'warning' },
  info: { rotulo: 'Informação', cor: 'default' },
}

function AlertasAgente({ alertas }: { alertas: AlertaAgente[] }) {
  return (
    <Card variant="outlined"><CardContent>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Avisos da última rodada</Typography>
      <Table size="small">
        <TableHead><TableRow><TableCell>Loja</TableCell><TableCell>Gravidade</TableCell><TableCell>Aviso</TableCell><TableCell>Anúncio</TableCell></TableRow></TableHead>
        <TableBody>
          {alertas.map((x, k) => (
            <TableRow key={k}>
              <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{x.loja}</TableCell>
              <TableCell><Chip size="small" color={GRAVIDADE[x.gravidade]?.cor || 'default'} label={GRAVIDADE[x.gravidade]?.rotulo || x.gravidade} /></TableCell>
              <TableCell sx={{ fontSize: 13 }}>{x.mensagem}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{x.link ? <Link href={x.link} target="_blank" rel="noopener noreferrer">{x.sku || 'abrir'}</Link> : null}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent></Card>
  )
}

// ---------------------------------------------------------------- página do agente

function DetalheAgente({ id, escreve, onVoltar }: { id: string; escreve: boolean; onVoltar: () => void }) {
  const [a, setA] = useState<Agente | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const carregar = useCallback(() => {
    fetchAgente(id).then((r) => setA(r.agente)).catch((e) => setErro(msg(e)))
  }, [id])
  useEffect(() => {
    carregar()
    const t = window.setInterval(carregar, 15000)
    return () => window.clearInterval(t)
  }, [carregar])
  if (erro) return <Alert severity="error">{erro}</Alert>
  if (!a) return <CircularProgress size={20} />
  const s = SEMAFORO[a.semaforo] || SEMAFORO.vermelho
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button size="small" onClick={onVoltar}>← Todos os agentes</Button>
        <Typography variant="h6">{a.nome}</Typography>
        <Chip size="small" color={s.cor} label={s.rotulo} />
        <Typography variant="caption" color="text.secondary">{a.agenda} · última {quando(a.ultima_rodada)} · próxima {a.pausado ? 'pausado' : quando(a.proxima)}</Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" component={Link} href={a.tela} variant="text">Abrir a tela do agente</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary">{a.objetivo}</Typography>
      {a.avisos.map((x) => <Alert key={x} severity="warning" sx={{ py: 0 }}>{x}</Alert>)}
      {Object.keys(a.parametros || {}).length ? (
        <Typography variant="body2"><b>Parâmetros:</b> {Object.entries(a.parametros).map(([k, v]) => `${k} = ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join(' · ')}</Typography>
      ) : null}
      {a.id === 'monitor_margens' ? <FilaMonitorMargens escreve={escreve} /> : null}
      {a.alertas?.length ? <AlertasAgente alertas={a.alertas} /> : null}
      {a.relatorio_hoje ? (
        <Card variant="outlined"><CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Relatório de hoje</Typography>
          <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 420, overflow: 'auto' }}>{a.relatorio_hoje}</Box>
        </CardContent></Card>
      ) : null}
      {a.historico?.length ? (
        <Card variant="outlined"><CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Histórico de rodadas</Typography>
          <Table size="small">
            <TableHead><TableRow><TableCell>Quando</TableCell><TableCell>Resultado</TableCell></TableRow></TableHead>
            <TableBody>
              {a.historico.slice(0, 30).map((h, k) => (
                <TableRow key={k}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{quando(String(h.quando || ''))}{h.dry_run ? ' (dry-run)' : ''}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{h.resumo ? String(h.resumo) : Array.isArray(h.contas) ? (h.contas as Array<Record<string, unknown>>).map((c) => `${c.conta}: ${c.executados ?? 0} ok · ${c.pulados ?? 0} reprovados · ${c.recusados ?? 0} recusados${c.saidas ? ` · ${c.saidas} saídas` : ''}`).join(' | ') : JSON.stringify(h).slice(0, 200)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>
      ) : null}
      {(a.log_rodar_agora?.length || a.log?.length) ? (
        <Card variant="outlined"><CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Log {a.rodar_agora?.ativo ? '· rodando agora…' : ''}</Typography>
          <Box component="pre" sx={{ m: 0, fontSize: 11, whiteSpace: 'pre-wrap', maxHeight: 360, overflow: 'auto', color: 'text.secondary' }}>{[...(a.log_rodar_agora || []), ...(a.log || [])].slice(-80).join('\n')}</Box>
        </CardContent></Card>
      ) : null}
    </Stack>
  )
}

// ---------------------------------------------------------------- página

export function AgentesPage() {
  const nav = useNavigate()
  const { agenteId } = useParams<{ agenteId?: string }>()
  const [agentes, setAgentes] = useState<Agente[] | null>(null)
  const [email, setEmail] = useState<EmailInfo | null>(null)
  const [escreve, setEscreve] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [emailMsg, setEmailMsg] = useState<string | null>(null)

  const carregar = useCallback(() => {
    fetchAgentes()
      .then((r) => {
        setAgentes(r.agentes)
        setEmail(r.email)
        setEscreve(r.escrita_liberada)
        setErro(null)
      })
      .catch((e) => setErro(e instanceof AgentesApiError ? e.message : msg(e)))
  }, [])
  useEffect(() => {
    carregar()
    const t = window.setInterval(carregar, 30000)
    return () => window.clearInterval(t)
  }, [carregar])

  const idNorm = agenteId ? agenteId.replace(/-/g, '_') : null
  const ok = agentes?.filter((a) => a.semaforo === 'verde').length ?? 0
  const atencao = agentes?.filter((a) => a.semaforo === 'amarelo').length ?? 0
  const falha = agentes?.filter((a) => a.semaforo === 'vermelho').length ?? 0
  const pend = Number(agentes?.find((a) => a.id === 'monitor_margens')?.numeros?.pendentes ?? 0)

  return (
    <DomainPageShell
      title="Agentes"
      subtitle="Os trabalhos que rodam sozinhos na VPS com as suas regras: o que cada um fez, quando roda de novo, e o interruptor."
      loading={!agentes && !erro}
      error={erro}
      onRetry={carregar}
      stats={[
        { label: 'Agentes OK', value: String(ok), hint: `${atencao} com atenção · ${falha} parados ou com falha` },
        { label: 'Esperando você', value: String(pend), hint: 'anúncios na fila do Monitor de margens' },
        { label: 'E-mail diário', value: email ? (email.configurado ? '07:00' : 'sem SMTP') : '…', hint: email?.para ? `para ${email.para}` : 'configure FIESTA_SMTP_* no serviço' },
      ]}
    >
      {idNorm && agentes ? (
        <DetalheAgente id={idNorm} escreve={escreve} onVoltar={() => nav('/agentes')} />
      ) : (
        <Stack spacing={2}>
          {!escreve && agentes ? <Alert severity="info">Serviço em modo leitura (sem token de escrita): interruptores e "rodar agora" desligados.</Alert> : null}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {(agentes || []).map((a) => (
              <CardAgente key={a.id} a={a} escreve={escreve} onMudou={carregar} onAbrir={() => nav(`/agentes/${a.id.replace(/_/g, '-')}`)} />
            ))}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">
              E-mail diário às 07:00 com este resumo{email?.para ? ` para ${email.para}` : ''}.
            </Typography>
            <Button size="small" variant="text" disabled={!escreve || !email?.configurado} onClick={() => { setEmailMsg('enviando…'); enviarEmailAgora().then((r) => setEmailMsg(`enviado para ${r.para}`)).catch((e) => setEmailMsg(msg(e))) }}>
              Enviar agora
            </Button>
            {emailMsg ? <Typography variant="caption">{emailMsg}</Typography> : null}
          </Stack>
        </Stack>
      )}
    </DomainPageShell>
  )
}
